import { useSessionStore } from '@/core/stores/sessionStore';
// core/services/postsService.ts — B4a: API-backed feed + engagement.
//
// Thin fetch wrappers over the DRF /api/posts/ endpoints (B3 apiClient). Each call
// hydrates the existing usePostStore (the store is a server cache); hooks/components
// are unchanged. Per-user cheer state is driven by the server's `cheered` field.

import type {
  Post,
  PostMedia,
  PollDuration,
  PostAnalytics,
  PostThreadResponse,
} from '@/core/models/post';
import { usePostStore } from '@/core/stores/postStore';
import { useForYouStore } from '@/core/stores/forYouStore';
import { useFeedStore, type FeedPage, type PagedFeedKey } from '@/core/stores/feedStore';
import { seedPosts } from '@/core/data/posts';
import { api, ApiError } from '@/core/lib/apiClient';
import { track } from '@/core/lib/analytics';

// Feature 3 (F9, 2026-08-10 adversarial review): ids already reported seen
// THIS SESSION (module-scoped, reset only by a hard reload or _resetForTest).
// Without this, switching tabs away from and back to For You, or a
// pull-to-refresh loop, re-reports the same ids on every fetch — burning the
// `seen: 120/hour` server throttle on pure duplicates instead of the new ids
// a real scroll session would produce.
const seenThisSession = new Set<string>();

/** Posts per page on the paged Home tabs (the server allows up to 60). */
export const FEED_PAGE_SIZE = 30;

export const postsService = {
  /**
   * `feed: 'for-you'` requests the viewer's topic-filtered algorithmic feed and
   * merges the results into the store WITHOUT clobbering it — the other tabs
   * (Following/Trending/Discover) filter client-side from the same store, and a
   * full replace here would make posts they need vanish when the viewer
   * switches back. The default (no `feed`) call keeps its original full-replace
   * behavior — that's the one-time boot hydration for the whole feed.
   */
  async listFeed(opts?: { feed?: 'for-you' }): Promise<Post[]> {
    const qs = opts?.feed ? `?feed=${opts.feed}` : '';
    const owner = useSessionStore.getState().accounts.find((a) => a.useThisAsActive)?.userId;
    const posts = await api<Post[]>(`/posts/${qs}`);
    if (owner !== useSessionStore.getState().accounts.find((a) => a.useThisAsActive)?.userId)
      throw new Error('Account changed during loading');
    if (opts?.feed) {
      const store = usePostStore.getState();
      posts.forEach((p) => store.upsertPost(p));
      if (opts.feed === 'for-you') {
        // Feature 3: batch-report the returned post ids once per feed load, not per
        // scroll — fire-and-forget, failures are inconsequential (the seen signal
        // is a soft ranking demotion, never correctness-critical). F10: explicit
        // `postsService.markSeen` receiver, not `this.markSeen` — this object
        // literal can be destructured/passed around by callers (several already
        // do, e.g. `const { markSeen } = postsService`), which would silently
        // detach `this` and throw or no-op.
        void postsService.markSeen(posts.map((p) => p.id));
      }
    } else {
      usePostStore.getState().setPosts(posts);
    }
    return posts;
  },

  /**
   * One page of a server-computed Home tab (`GET /posts/?feed=<tab>&offset=&limit=`).
   *
   * Following is the people you follow, your own posts, and everything those people
   * spread; Discover is everyone you do not follow yet; Trending is recent engagement.
   * The posts are merged into postStore (a cache) and the tab's ordered id list goes to
   * feedStore: `offset` 0 replaces the tab, anything else appends the next page.
   */
  async listTab(key: PagedFeedKey, offset = 0): Promise<FeedPage> {
    const owner = useSessionStore.getState().accounts.find((a) => a.useThisAsActive)?.userId;
    const data = await api<{ results: Post[]; next: number | null }>(
      `/posts/?feed=${key}&offset=${offset}&limit=${FEED_PAGE_SIZE}`,
    );
    if (owner !== useSessionStore.getState().accounts.find((a) => a.useThisAsActive)?.userId)
      throw new Error('Account changed during loading');
    const store = usePostStore.getState();
    const spreadBy: FeedPage['spreadBy'] = {};
    data.results.forEach((p) => {
      store.upsertPost(p);
      if (p.spreadBy) spreadBy[p.id] = p.spreadBy;
    });
    const page: FeedPage = { ids: data.results.map((p) => p.id), next: data.next, spreadBy };
    if (offset === 0) useFeedStore.getState().setFirstPage(key, page);
    else useFeedStore.getState().appendPage(key, page);
    return page;
  },

  /**
   * One page of a community's posts, newest first
   * (`GET /posts/?feed=community&community=<slug>&offset=&limit=`). Public, like the
   * community page. The posts are merged into postStore; the caller keeps the order.
   */
  async listCommunity(slug: string, offset = 0): Promise<{ ids: string[]; next: number | null }> {
    const owner = useSessionStore.getState().accounts.find((a) => a.useThisAsActive)?.userId;
    const data = await api<{ results: Post[]; next: number | null }>(
      `/posts/?feed=community&community=${encodeURIComponent(slug)}&offset=${offset}&limit=${FEED_PAGE_SIZE}`,
    );
    // Same guard as listTab: a page fetched for one account never lands in another's store.
    if (owner !== useSessionStore.getState().accounts.find((a) => a.useThisAsActive)?.userId)
      throw new Error('Account changed during loading');
    const store = usePostStore.getState();
    data.results.forEach((p) => store.upsertPost(p));
    return { ids: data.results.map((p) => p.id), next: data.next };
  },

  /**
   * Feature 3 (seen-post tracking): report post ids as "rendered in the
   * viewer's For-You feed". Capped at 50/call server-side; chunk here so a
   * larger batch from the client still gets fully reported instead of
   * silently truncated. Deduped against `seenThisSession` (F9) — an id
   * already reported this session is dropped before any request is made, so
   * repeat fetches of the same feed page never re-spend the server throttle.
   */
  async markSeen(ids: string[]): Promise<void> {
    const fresh = ids.filter((id) => !seenThisSession.has(id));
    if (fresh.length === 0) return;
    const chunkSize = 50;
    for (let i = 0; i < fresh.length; i += chunkSize) {
      const chunk = fresh.slice(i, i + chunkSize);
      try {
        await api<{ marked: number }>('/posts/seen/', { method: 'POST', body: { ids: chunk } });
        chunk.forEach((id) => seenThisSession.add(id));
      } catch {
        // fire-and-forget — see docstring above. Not added to seenThisSession on
        // failure, so a genuinely-failed report can still be retried later.
      }
    }
  },

  async getPost(id: string): Promise<Post | undefined> {
    try {
      const post = await api<Post>(`/posts/${id}/`);
      // upsert, not patch: on a cold store (deep link / hard reload on /post/:id) the post
      // is not in the feed yet and patchPost would no-op, rendering a false "not found".
      usePostStore.getState().upsertPost(post);
      return post;
    } catch {
      return undefined;
    }
  },

  /** Video posts only (1.27.50). Replaces your Cherish, as a Cherish replaces your dislike. */
  async toggleDislike(id: string): Promise<Post | undefined> {
    const post = await api<Post>(`/posts/${id}/dislike/`, { method: 'POST' });
    usePostStore.getState().patchPost(id, post);
    return post;
  },

  /**
   * Toggle the viewer's Cherish. Optimistic: the button and the count change at once,
   * the server's answer then replaces the guess, and a failure puts the post back
   * exactly as it was and rethrows so the caller can say so.
   */
  async toggleCheer(id: string): Promise<Post | undefined> {
    const before = usePostStore.getState().posts.find((p) => p.id === id);
    if (before) {
      const on = !before.cheered;
      const cheers = Math.max(0, before.cheers + (on ? 1 : -1));
      usePostStore.getState().patchPost(id, {
        cheered: on,
        cheers,
        liked: on,
        likes: cheers,
        // A Cherish replaces your thumbs-down on a video.
        ...(on && before.disliked
          ? { disliked: false, dislikes: Math.max(0, (before.dislikes ?? 1) - 1) }
          : {}),
      });
    }
    try {
      const post = await api<Post>(`/posts/${id}/cheer/`, { method: 'POST' });
      usePostStore.getState().patchPost(id, post);
      return post;
    } catch (e) {
      if (before) {
        usePostStore.getState().patchPost(id, {
          cheered: before.cheered,
          cheers: before.cheers,
          liked: before.liked,
          likes: before.likes,
          disliked: before.disliked,
          dislikes: before.dislikes,
        });
      }
      throw e;
    }
  },

  /**
   * Toggle Spread-It!. Spreading puts the post in your followers' Following feed (and
   * your own); spreading again takes it back. Optimistic, with the same rollback as
   * toggleCheer. (The "X spread this" label lives in feedStore, so it is untouched.)
   */
  async spread(id: string): Promise<Post | undefined> {
    const before = usePostStore.getState().posts.find((p) => p.id === id);
    if (before) {
      const on = !before.spread;
      usePostStore.getState().patchPost(id, {
        spread: on,
        spreads: Math.max(0, before.spreads + (on ? 1 : -1)),
      });
    }
    try {
      const post = await api<Post>(`/posts/${id}/spread/`, { method: 'POST' });
      usePostStore.getState().patchPost(id, post);
      // Your own spread shows in your Following feed straight away.
      if (post.spread) useFeedStore.getState().prependId('following', id);
      return post;
    } catch (e) {
      if (before) {
        usePostStore.getState().patchPost(id, { spread: before.spread, spreads: before.spreads });
      }
      throw e;
    }
  },

  async createPost(input: {
    attachments?: import('@/features/home/composer/studio/contracts').AuthoredAttachments;
    question?: boolean;
    audienceRing?: { id: string; revision: number };
    perspectiveSplit?: { slug: string; revision: number };
    collaborativeDraft?: { slug: string; revision: number; version: number };
    body: string;
    media?: PostMedia;
    mediaKey?: string;
    /** Uploaded images in display order. Supersedes `mediaKey` for multi-image posts. */
    images?: { key: string; alt?: string; width?: number; height?: number }[];
    /** Video phase 2: the id of the member's own READY, unposted clip. A post carries images
     * OR a video, and no poll alongside a video (all enforced server-side). */
    videoId?: string;
    /** Videos page (2026-09-26): the clip's optional title, category ('' = none) and
     * Short flag (clips of 60 s or less only — enforced server-side). */
    videoTitle?: string;
    videoCategory?: import('@/core/models/post').VideoCategoryId | '';
    videoShort?: boolean;
    /** 1.27.60 Clip: the source post and the section (5–60 s) — see PostClip. */
    clipOf?: string;
    clipStartMs?: number;
    clipEndMs?: number;
    /** 1.27.60 uploader permissions (server defaults: download off, embed + clips on). */
    videoAllowDownload?: boolean;
    videoAllowEmbed?: boolean;
    videoAllowClips?: boolean;
    /** Audience — omit for the default 'everyone' ("my channel's default" on a video).
     * 'public' and 'unlisted' are video-only (400 `video_only` otherwise). */
    audience?: import('@/core/models/post').AudienceType;
    regions?: string[];
    people?: string[];
    /** Poll — mutually exclusive with images (enforced server-side, mirrored client-side). */
    poll?: { options: string[]; duration: PollDuration };
    /** Future ISO datetime — a scheduled post, unpublished until then. */
    scheduledAt?: string;
    /** 1.23.00 Phase 5: an explicit draft — unpublished, no scheduledAt.
     * Mutually exclusive with scheduledAt (rejected server-side if both are
     * set). Free for everyone — not a Pro gate. */
    draft?: boolean;
    /** Quote posts (1.21.00 Slice 2): the id (or share code) of the post being quoted. */
    quotedPostId?: string;
    /**
     * Threads (1.24.00 C2): the id (or share code) of the post this one
     * continues. The server normalises it to the thread's root and assigns the
     * position. Two rules it enforces, mirrored in the Composer so a member
     * never trips them: a part INHERITS the root's audience (sending an
     * `audience` alongside is a 400 `threadRootId: 'inherits_audience'`), and a
     * part cannot also be a quote (400 `threadRootId: 'thread_and_quote'`).
     */
    threadRootId?: string;
  }): Promise<Post> {
    const post = await api<Post>('/posts/', {
      method: 'POST',
      body: {
        body: input.body,
        ...(input.attachments ? { attachments: input.attachments } : {}),
        ...(input.question ? { question: true } : {}),
        ...(input.audienceRing ? { audienceRing: input.audienceRing } : {}),
        ...(input.perspectiveSplit ? { perspectiveSplit: input.perspectiveSplit } : {}),
        ...(input.collaborativeDraft ? { collaborativeDraft: input.collaborativeDraft } : {}),
        media: input.media ?? '',
        media_key: input.mediaKey ?? '',
        ...(input.images?.length ? { images: input.images } : {}),
        ...(input.videoId
          ? {
              videoId: input.videoId,
              ...(input.videoTitle !== undefined ? { videoTitle: input.videoTitle } : {}),
              ...(input.videoCategory !== undefined ? { videoCategory: input.videoCategory } : {}),
              ...(input.videoShort !== undefined ? { videoShort: input.videoShort } : {}),
              ...(input.videoAllowDownload !== undefined
                ? { videoAllowDownload: input.videoAllowDownload }
                : {}),
              ...(input.videoAllowEmbed !== undefined
                ? { videoAllowEmbed: input.videoAllowEmbed }
                : {}),
              ...(input.videoAllowClips !== undefined
                ? { videoAllowClips: input.videoAllowClips }
                : {}),
            }
          : {}),
        ...(input.clipOf
          ? { clipOf: input.clipOf, clipStartMs: input.clipStartMs, clipEndMs: input.clipEndMs }
          : {}),
        ...(input.audience ? { audience: input.audience } : {}),
        ...(input.regions?.length ? { regions: input.regions } : {}),
        ...(input.people?.length ? { people: input.people } : {}),
        ...(input.poll ? { poll: input.poll } : {}),
        ...(input.scheduledAt ? { scheduledAt: input.scheduledAt } : {}),
        ...(input.draft ? { draft: true } : {}),
        ...(input.quotedPostId ? { quotedPostId: input.quotedPostId } : {}),
        ...(input.threadRootId ? { threadRootId: input.threadRootId } : {}),
      },
    });
    // A scheduled draft isn't part of the public feed yet — don't inject it at
    // the top of everyone's timeline. It only shows on the author's own
    // feed/profile via the store's post list once fetched there.
    if (post.published !== false) {
      usePostStore.getState().prependPost(post);
      // Keep the For You tab's server-picked id list current — a no-op if the
      // viewer hasn't opened that tab this session (ids still null).
      useForYouStore.getState().prependId(post.id);
      // Following is the default tab and includes your own posts: the post you just
      // published has to be on the screen you are looking at.
      useFeedStore.getState().prependId('following', post.id);
      // Funnel step (1.24.60, Umami): published posts only — drafts and
      // scheduled posts haven't reached anyone yet. "First post per member"
      // is derived at analysis time; the client never decides what's first.
      track('post-published');
    } else {
      usePostStore.getState().upsertPost(post);
    }
    return post;
  },

  /**
   * Cast/replace the viewer's poll vote. Re-voting replaces the prior pick.
   * Optimistic: patches the store with a predicted tally immediately, then
   * reconciles with the server's response. On failure (incl. a 409 for a
   * closed/deleted/unpublished poll) the store is rolled back to the
   * pre-vote snapshot and the ApiError/Error rethrows for the caller.
   */
  async pollVote(postId: string, optionId: string): Promise<Post> {
    const store = usePostStore.getState();
    const before = store.posts.find((p) => p.id === postId);
    const prevPoll = before?.poll ?? null;

    if (prevPoll) {
      const prevOptionId = prevPoll.viewerOptionId;
      const optimisticTotal = prevOptionId ? prevPoll.totalVotes : prevPoll.totalVotes + 1;
      const options = prevPoll.options.map((o) => {
        let votes = o.votes;
        if (o.id === prevOptionId && o.id !== optionId) votes -= 1;
        if (o.id === optionId && o.id !== prevOptionId) votes += 1;
        return { ...o, votes };
      });
      const withPct = options.map((o) => ({
        ...o,
        pct: optimisticTotal > 0 ? Math.round((o.votes / optimisticTotal) * 100) : 0,
      }));
      store.patchPost(postId, {
        poll: {
          ...prevPoll,
          options: withPct,
          totalVotes: optimisticTotal,
          viewerOptionId: optionId,
        },
      });
    }

    try {
      const post = await api<Post>(`/posts/${postId}/poll/vote/`, {
        method: 'POST',
        body: { optionId },
      });
      usePostStore.getState().patchPost(postId, post);
      return post;
    } catch (e) {
      // Same guard as the optimistic branch above — no optimistic write happened
      // for a poll-less record (prevPoll null), so there's nothing to roll back.
      // A bare `if (before)` would write `poll: null` onto a record that never
      // had one.
      if (before && prevPoll) usePostStore.getState().patchPost(postId, { poll: prevPoll });
      throw e;
    }
  },

  /**
   * Author-only, text-only edit. Mirrors commentsService.updateComment.
   * Returns the updated Post, `'edit_window_expired'` when a free account's
   * 30-minute post-publish window has passed (see
   * useOwnPostActions.ts — the client checks this ahead of time, this is
   * the server-authoritative fallback for clock skew / a stale mount), or
   * null on any other failure.
   */
  async editPost(id: string, body: string): Promise<Post | 'edit_window_expired' | null> {
    try {
      const post = await api<Post>(`/posts/${id}/`, { method: 'PATCH', body: { body } });
      usePostStore.getState().patchPost(id, post);
      return post;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'edit_window_expired') return 'edit_window_expired';
      return null;
    }
  },

  /** Author-only soft delete. The post STAYS in the store (labeled), unlike hidePost. */
  async deletePost(id: string): Promise<Post | null> {
    try {
      const post = await api<Post>(`/posts/${id}/`, { method: 'DELETE' });
      usePostStore.getState().patchPost(id, post);
      return post;
    } catch {
      return null;
    }
  },

  /** 1.23.00 Phase 5: author-only "publish now" for a draft or not-yet-due
   * scheduled post. Free for everyone — not a Pro gate. */
  async publishPost(id: string): Promise<Post | null> {
    try {
      const post = await api<Post>(`/posts/${id}/publish/`, { method: 'POST' });
      usePostStore.getState().patchPost(id, post);
      return post;
    } catch {
      return null;
    }
  },

  /** 1.23.00 Phase 5: author-only "cancel schedule" — converts a scheduled
   * post back into a draft (kept, not deleted). */
  async unschedulePost(id: string): Promise<Post | null> {
    try {
      const post = await api<Post>(`/posts/${id}/unschedule/`, { method: 'POST' });
      usePostStore.getState().patchPost(id, post);
      return post;
    } catch {
      return null;
    }
  },

  /**
   * "Not interested in this post" — persists server-side and removes it from the
   * local feed immediately (optimistic; the server also excludes it going forward).
   * The endpoint toggles, so calling this again on an already-hidden post unhides
   * it — see `unhidePost`, which is what actually powers the "Undo" toast
   * affordance in PostMoreMenu (this method only removes; it does not restore).
   * On a failed request, the local store is left untouched and this resolves to
   * false, so the caller knows not to treat the post as hidden.
   */
  /**
   * 1.27.60: the uploader changes a posted video's visibility and permissions
   * (author-only; the server applies an audience change to the whole thread).
   */
  async videoSettings(
    id: string,
    body: {
      audience?: import('@/core/models/post').AudienceType;
      regions?: string[];
      people?: string[];
      videoAllowDownload?: boolean;
      videoAllowEmbed?: boolean;
      videoAllowClips?: boolean;
    },
  ): Promise<Post> {
    const post = await api<Post>(`/posts/${id}/video-settings/`, { method: 'PATCH', body });
    usePostStore.getState().patchPost(id, post);
    return post;
  },

  async hidePost(id: string): Promise<boolean> {
    try {
      await api<{ hidden: boolean }>(`/posts/${id}/hide/`, { method: 'POST' });
      usePostStore.getState().removePost(id);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Undoes a hide: re-toggles the server-side flag off and, by default,
   * restores `post` into the local store (hidePost already removed it, so
   * the caller must supply the post object it still has in hand — e.g. from
   * the menu's own props). `index` restores its original list position when
   * the caller knows it (the ··· menu's Undo toast path).
   *
   * The endpoint TOGGLES — it does not know "unhide" from "hide", it just
   * flips whatever is there. If the post was already unhidden elsewhere
   * (another tab, a stale Settings row) by the time this call lands, this
   * call RE-HIDES it instead. Returns the server's actual resulting `hidden`
   * flag so the caller can tell the two apart, mirroring toggleMute's
   * true/false/null contract:
   *   false — genuinely unhidden.
   *   true  — stale toggle: the post is hidden again. Never restored to the
   *           store regardless of `restoreToFeed` — there is nothing to
   *           restore, the post really is hidden.
   *   null  — the request itself failed; store untouched.
   *
   * `restoreToFeed` (default true) exists for the Settings manage list: it
   * must NOT inject a possibly-days-old post into the live home feed store
   * (Settings wants only the server-side toggle and its own local row
   * removed). No current caller passes `index` — PostMoreMenu's Undo toast
   * calls this bare (`unhidePost(post)`), so restorePost's no-index branch
   * prepends the post back to the top of the feed rather than reinserting it
   * at a remembered position; `index` stays available for a future caller
   * that tracks one.
   */
  async unhidePost(
    post: Post,
    { index, restoreToFeed = true }: { index?: number; restoreToFeed?: boolean } = {},
  ): Promise<boolean | null> {
    try {
      const r = await api<{ hidden: boolean }>(`/posts/${post.id}/hide/`, { method: 'POST' });
      if (r.hidden === false && restoreToFeed) {
        usePostStore.getState().restorePost(post, index);
      }
      return r.hidden;
    } catch {
      return null;
    }
  },

  /**
   * The signed-in user's hidden ("Not interested") posts, newest-hidden-first —
   * the manage-list counterpart to hidePost/unhidePost, for Settings → Content
   * Preferences. Deliberately does NOT touch usePostStore: this list is
   * Settings-only, separate from the home feed cache, and must keep showing a
   * post the caller just unhid until the hook removes its own local row.
   */
  async hiddenPosts(): Promise<Post[]> {
    return api<Post[]>('/posts/me/hidden-posts/');
  },

  /**
   * 1.24.00 C2: the whole thread a post belongs to — `{root, rootUnavailable,
   * parts}`, parts in position order — for EITHER the root's id/share code or
   * any part's. See PostThreadResponse for the three answers this endpoint
   * gives and what each one means; `root` is null on one of them WITHOUT the
   * thread being gone, so callers must read `rootUnavailable` rather than
   * treating a null root as "nothing to show".
   *
   * Deliberately NOT written into usePostStore: this is the detail page's own
   * view of a thread, and injecting its parts into the shared feed store would
   * put posts into the timeline that no feed fetch ever returned.
   *
   * Returns null on any failure, including the 404 the server gives for a
   * thread this viewer may not read at all — the caller renders nothing rather
   * than an error, since a missing thread is not a broken page. A DELETED root
   * is no longer that case: it answers 200 with `rootUnavailable: true` and the
   * surviving parts.
   */
  async postThread(ident: string): Promise<PostThreadResponse | null> {
    try {
      return await api<PostThreadResponse>(`/posts/${ident}/thread/`);
    } catch {
      return null;
    }
  },

  /**
   * 1.23.00 Phase 7: author-only, Pro-gated basic post analytics. Returns
   * null on any failure (not Pro, not the author, signed out, etc.) — the
   * caller (PostMoreMenu's analytics view) shows an upsell/explanation
   * rather than surfacing the raw error code, since a free author reaching
   * this at all already got a "Pro" gate on the menu item itself; null here
   * is the defensive fallback for a stale mount / lapsed subscription mid-view.
   */
  async postAnalytics(id: string): Promise<PostAnalytics | null> {
    try {
      return await api<PostAnalytics>(`/posts/${id}/analytics/`);
    } catch {
      return null;
    }
  },

  _resetForTest(): void {
    usePostStore.getState().setPosts(seedPosts.map((p) => ({ ...p })));
    seenThisSession.clear();
  },
} as const;
